import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Table } from 'react-bootstrap';
import Time from 'react-time';
import { getOrgTermsContent } from '../../../content/utils.js';
import {
  ChangeImpactLink,
  PortfolioInfoLink,
  ProjectLink,
} from '../../links/index.jsx';
import { IMPACT_ACTION_PLAN_STATUSES } from '../../../lib/schema.js';
import RatingLabel from '../../RatingLabel.jsx';
import OwnersList from '../../OwnersList.jsx';
import VerticalSeparator from '../../VerticalSeparator.jsx';
import { selectOrganization } from '../../../modules/organizations.js';
import styles from './ChangeImpactTable.module.css';

// Rows from the organization search have flat fields (projectId, projectName,
// parentPortfolioName); rows from the employee profile API have nested objects
// (project, portfolio, parentPortfolio).
const getProjectId = (ci) => ci.projectId ?? ci.project?.id;
const getProjectName = (ci) => ci.projectName ?? ci.project?.name;

const ChangeImpactRow = ({
  changeImpact,
  showAllOwners,
  linkOwnersToEmployee,
}) => {
  return (
    <React.Fragment>
      <tr key={changeImpact.id} className={styles.changeImpactDataRow}>
        <td>
          {changeImpact.changeImpactType ? (
            <strong
              className={
                changeImpact.changeImpactType.archived ? 'text-muted' : ''
              }
            >
              {changeImpact.changeImpactType.name}
            </strong>
          ) : (
            '—'
          )}
        </td>
        <td>
          <strong>
            <RatingLabel reverse rating={changeImpact.scale} />
          </strong>
        </td>
        <td>
          <strong>
            <RatingLabel rating={changeImpact.perception} />
          </strong>
        </td>
        <td>
          {changeImpact.owners.length ? (
            <OwnersList
              owners={changeImpact.owners}
              showAll={showAllOwners}
              linkToEmployee={linkOwnersToEmployee}
            />
          ) : (
            '—'
          )}
        </td>
        <td>
          <strong className="flex flex-col flex-align-start">
            {changeImpact.startDate && changeImpact.endDate ? (
              <React.Fragment>
                <span>
                  <Time value={changeImpact.startDate} format="M/D/YY" />
                </span>
                <span>
                  <Time value={changeImpact.endDate} format="M/D/YY" />
                </span>
              </React.Fragment>
            ) : (
              '—'
            )}
          </strong>
        </td>
        <td>
          <strong>{changeImpact.actionPlanStatus || '—'}</strong>
        </td>
        <td style={{ textAlign: 'right' }}>
          <strong>{changeImpact.tasksNumber}</strong>
        </td>
      </tr>
    </React.Fragment>
  );
};

const ChangeImpactTable = ({
  changeImpacts,
  slug,
  portfolios = [],
  projects = [],
  hasParentPortfolios,
  showAllOwners = false,
  // owner names link to the employee profile (employee profile Change Impacts tab)
  linkOwnersToEmployee = false,
}) => {
  const getPortfolioEntity = (ci) => {
    // portfolio sent with the row: { id, name, canRead } or null
    if (ci.portfolio !== undefined) {
      return ci.portfolio
        ? { ...ci.portfolio, permissions: { can_read: ci.portfolio.canRead } }
        : null;
    }

    const projectEntity = projects.find((p) => p.id === ci.projectId);
    if (!projectEntity) return null;
    const portfolioEntity = portfolios.find(
      (p) => p.id === projectEntity.portfolioId
    );
    return portfolioEntity || null;
  };

  const renderParentPortfolio = (ci) => {
    const parentPortfolio =
      ci.parentPortfolio !== undefined
        ? ci.parentPortfolio
        : ci.parentPortfolioName && { name: ci.parentPortfolioName };
    if (!parentPortfolio) return '—';

    // TODO: render as a link when parentPortfolio.id is present and a read-only
    // parent portfolio link exists (only EditParentPortfolioLink exists today).
    return <span>{parentPortfolio.name}</span>;
  };

  return (
    <Table hover>
      <thead>
        <tr>
          <th colSpan={7}>Title</th>
        </tr>
      </thead>
      <tbody>
        {changeImpacts.map((changeImpact) => (
          <React.Fragment key={changeImpact.id}>
            <tr>
              <td colSpan={7}>
                <h3 className="flex m-b-10">
                  <ChangeImpactLink
                    projectId={getProjectId(changeImpact)}
                    changeImpactId={changeImpact.id}
                  >
                    {changeImpact.title}
                  </ChangeImpactLink>
                </h3>
                <div className="flex">
                  <div className="flex header-item">
                    <span className="text-muted m-r-12 text-nowrap">
                      {getOrgTermsContent('project')}:{' '}
                    </span>
                    <ProjectLink projectId={getProjectId(changeImpact)}>
                      {getProjectName(changeImpact)}
                    </ProjectLink>
                  </div>
                  <VerticalSeparator />
                  <div className="flex header-item">
                    <span className="text-muted m-r-12 text-nowrap">
                      {getOrgTermsContent('portfolio')}:{' '}
                    </span>
                    {(() => {
                      const portfolioEntity = getPortfolioEntity(changeImpact);
                      if (!portfolioEntity) return '—';

                      return portfolioEntity.permissions &&
                        portfolioEntity.permissions.can_read ? (
                        <PortfolioInfoLink portfolioId={portfolioEntity.id}>
                          {portfolioEntity.name}
                        </PortfolioInfoLink>
                      ) : (
                        <strong>{portfolioEntity.name}</strong>
                      );
                    })()}
                  </div>
                  {hasParentPortfolios ? (
                    <React.Fragment>
                      <VerticalSeparator />
                      <div className="flex header-item">
                        <span className="text-muted m-r-12 text-nowrap">
                          {getOrgTermsContent('parentPortfolio')}:{' '}
                        </span>
                        {renderParentPortfolio(changeImpact)}
                      </div>
                    </React.Fragment>
                  ) : null}
                </div>
              </td>
            </tr>
            <tr>
              <th>Type</th>
              <th style={{ minWidth: '140px' }}>Scale</th>
              <th style={{ minWidth: '140px' }}>Perception</th>
              <th>Owner</th>
              <th>Dates</th>
              <th>Action Status</th>
              <th style={{ textAlign: 'right' }}>Tasks</th>
            </tr>
            <ChangeImpactRow
              changeImpact={changeImpact}
              showAllOwners={showAllOwners}
              linkOwnersToEmployee={linkOwnersToEmployee}
            />
          </React.Fragment>
        ))}
      </tbody>
    </Table>
  );
};

ChangeImpactTable.propTypes = {
  changeImpact: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string.isRequired,
      projectName: PropTypes.string.isRequired,
      type: PropTypes.string.isRequired,
      scale: PropTypes.string.isRequired,
      perception: PropTypes.string.isRequired,
      status: PropTypes.oneOf(IMPACT_ACTION_PLAN_STATUSES).isRequired,
      owner: PropTypes.shape({
        fullName: PropTypes.string.isRequired,
      }).isRequired,
    })
  ),
};

const mapStateToProps = (state) => ({
  hasParentPortfolios: selectOrganization(state).hasParentPortfolios,
});

export default connect(mapStateToProps)(ChangeImpactTable);
